import { Meta } from '@/components/layout/Layout';
import { OpportunitySalary } from './opportunity';
import { ContentApiRefDataItem, RefDataItem } from './refData';
import { RelatedSearchLandingPage } from './search';

export enum ImageTypes {
  gatsby = 'gatsby',
  img = 'img',
  html = 'html'
}

export type Image = {
  type: ImageTypes;
  path: string;
  alt: string;
  image?: any;
};

export type FAQ = {
  question: string;
  answer: string;
};

export enum AssociatedType {
  InPartnership = 'In Partnership with',
  SupportedBy = 'Supported by',
  HostedBy = 'Hosted by',
  AdvertisedBy = 'Advertising feature by',
  EditorialAdvice = 'Editorial advice'
}

export enum ParagraphDataAssociatedType {
  field_in_partnership = 'field_in_partnership',
  field_supported_by = 'field_supported_by',
  field_organisation = 'field_organisation',
  field_advertised_by = 'field_advertised_by',
  field_education_provider = 'field_education_provider',
  organisation = 'organisation',
  educationProvider = 'educationProvider'
}

export enum ContentType {
  Article = 'article',
  Course = 'course',
  CourseProvider = 'course_provider',
  Event = 'event',
  GraduateProfile = 'graduate_profile',
  Opportunity = 'opportunity',
  Organisation = 'organisation',
  Video = 'video'
}

export type Sector = {
  id: string;
  label: string;
};

export type Content = {
  nid: string;
  uuid: string;
  type: string;
  title: string;
  body: string;
  abstract: string;
  path: string;
  featured: boolean;
  promoted: boolean;
  status: boolean;
};

export type Article = Content & {
  thumbnailImage: Image;
  featuredImage: Image;
  categories: RefDataItem[];
  sectors: RefDataItem[];
  primaryCtaLink: string;
  primaryCtaLabel: string;
  organisation: Organisation;
};

export type Organisation = Content & {
  headlineImage: Image;
  logo: Image;
  logoUrl: string;
  thumbnailImage: Image;
  featuredImage: Image;
  /** Plain string from the site build, ref data object from the content APIs. */
  organisationSize: string | ContentApiRefDataItem | null;
  graduatePositions: string;
  femalePercentage: string;
  foundedYear: string;
  numberOfJobs: number;
  numberOfEvents: number;
  sectors: RefDataItem[];
  status: boolean;
  externalId: string;
};

export type EducationProvider = Content & {
  headlineImage: Image;
  logo: Image;
  thumbnailImage: Image;
  featuredImage: Image;
};

export type Course = Content & {
  location: string;
  duration: string;
  educationProvider: EducationProvider;
  qualificationLetters: string;
  qualifications: string[];
  attendanceTypes: string[];
};

export type GraduateProfile = Content & {
  organisation: Organisation;
  profileType: string;
  graduateImage: Image;
};

export type Opportunity = Content & {
  organisation: Organisation;
  employmentType?: ContentApiRefDataItem | null;
  opportunityType: string;
  salary: OpportunitySalary;
  sectors: Sector[];
  location: string;
  city?: Array<{ label: string }>;
  locationType?: string;
  applicationDeadline: string;
  applicationOpenDate?: string | number;
  unpublishDate?: string | null;
  applicationUrl: string;
  applicationEmail: string;
  applicationOpen: boolean;
  opportunityStartDate: string[];
  benefits: string[];
  degreeSubjects: string[];
  degreeRequirements: string[];
  preregister: boolean;
  externalId: string;
  sourceOrganisationName: string;
  scraperJob: boolean;
  freeJob: boolean;
  created: Date;
  relatedSearchLandingPages: RelatedSearchLandingPage[];
  relatedGradsim?: {
    featuredImage: string;
    primaryCtaLabel: string;
    primaryCtaLink: string;
    thumbnailImage: string;
    title: string;
  };
  addresses?: {
    field_address_line: string;
    field_city_label: string;
    field_postcode: string;
    relationships: { field_city: { label: string }; field_country: { label: string } };
  }[];
  meta: Meta[];
  showVerifiedEmployerBadge: boolean;
};

export type Event = Content & {
  organisation: Organisation;
  educationProvider: EducationProvider;
  thumbnailImage: Image;
  featuredImage: Image;
  location: string;
  eventType: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  eventUrl: string;
  eventRegisterUrl: string;
  eventSeries: boolean;
  logo: Image;
  showDaysLeftToApply: boolean;
};

export type Video = Content & {
  organisation: Organisation;
  educationProvider: EducationProvider;
  headlineImage: Image;
  thumbnailImage: Image;
  featuredImage: Image;
  videoUrl: string;
  sectors: RefDataItem[];
};

export type Pathway = Content & {
  image: Image;
  duration: string;
  organisation: Organisation;
  educationProvider: EducationProvider;
  logo: Image;
  category: RefDataItem;
};

export type Publication = Content & {
  thumbnailImage: Image;
};
